import React, { useState, useEffect, useRef } from 'react';
import {
  Close as CloseIcon,
  Add as PlusIcon,
  Delete as TrashIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import {
  MenuBook as LibraryIcon
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  type DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { SalesPlaybookTag, SalesPlaybookIndustry, PlaybookLibraryType } from '../services/apiService';
import LibrarySelectPanel from './LibrarySelectPanel';

interface SortableBulletItemProps {
  id: number;
  value: string;
  placeholder: string;
  canRemove: boolean;
  onChange: (value: string) => void;
  onRemove: () => void;
}

const SortableBulletItem: React.FC<SortableBulletItemProps> = ({ id, value, placeholder, canRemove, onChange, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="flex gap-2 items-center"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab text-gray-300 hover:text-gray-500 flex-shrink-0 p-0.5 touch-none"
      >
        <DragIndicatorIcon style={{ fontSize: '1rem' }} />
      </button>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
      />
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded flex-shrink-0"
        >
          <TrashIcon style={{ fontSize: '1rem' }} />
        </button>
      )}
    </div>
  );
};

interface SortableQuestionItemProps {
  id: number;
  question: { text: string; tags: SalesPlaybookTag[] };
  index: number;
  canRemove: boolean;
  onTextChange: (value: string) => void;
  onRemove: () => void;
  onTagToggle: (tag: SalesPlaybookTag) => void;
  tagLabels: Record<SalesPlaybookTag, string>;
  allTags: SalesPlaybookTag[];
}

const SortableQuestionItem: React.FC<SortableQuestionItemProps> = ({
  id, question, index, canRemove, onTextChange, onRemove, onTagToggle, tagLabels, allTags
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="p-2 border border-gray-200 rounded-lg bg-gray-50"
    >
      <div className="flex gap-2 mb-2 items-start">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab text-gray-300 hover:text-gray-500 flex-shrink-0 p-0.5 mt-1 touch-none"
        >
          <DragIndicatorIcon style={{ fontSize: '1rem' }} />
        </button>
        <input
          type="text"
          value={question.text}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder={`Question ${index + 1}...`}
          className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
        />
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded flex-shrink-0"
          >
            <TrashIcon style={{ fontSize: '1rem' }} />
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1 pl-6">
        <span className="text-xs text-gray-500 mr-1">Tags:</span>
        {allTags.map(tag => (
          <button
            key={tag}
            type="button"
            onClick={() => onTagToggle(tag)}
            className={`px-1.5 py-0.5 text-xs rounded-full border transition-colors ${
              question.tags.includes(tag)
                ? 'bg-blue-100 text-blue-700 border-blue-300'
                : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'
            }`}
          >
            {tagLabels[tag]}
          </button>
        ))}
      </div>
    </div>
  );
};

const SALES_PLAYBOOK_TAGS: SalesPlaybookTag[] = [
  'situation',
  'pain',
  'cost_math',
  'authority',
  'timeline',
  'close_trigger'
];

const TAG_LABELS: Record<SalesPlaybookTag, string> = {
  situation: 'Situation',
  pain: 'Pain',
  cost_math: 'Cost Math',
  authority: 'Authority',
  timeline: 'Timeline',
  close_trigger: 'Close Trigger'
};

interface RoleData {
  id: string;
  displayName: string;
  pitchBullets: string[];
  advancedBullets: string[];
  questions: { text: string; tags: SalesPlaybookTag[] }[];
  isExpanded: boolean;
}

interface AddSalesPlaybookModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    industryDisplayName: string;
    roles: {
      originalId?: string; // Original role ID for matching during edit
      displayName: string;
      pitchBullets: string[];
      advancedBullets: string[];
      questions: { text: string; tags: SalesPlaybookTag[] }[];
    }[];
  }) => void;
  existingIndustries: string[];
  editingIndustry?: SalesPlaybookIndustry | null;
}

const AddSalesPlaybookModal: React.FC<AddSalesPlaybookModalProps> = ({
  isOpen,
  onClose,
  onSave,
  existingIndustries,
  editingIndustry
}) => {
  const [industryDisplayName, setIndustryDisplayName] = useState('');
  const [useMultipleRoles, setUseMultipleRoles] = useState(false);
  const [roles, setRoles] = useState<RoleData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Library selection state
  const [libraryPanelOpen, setLibraryPanelOpen] = useState(false);
  const [libraryPanelType, setLibraryPanelType] = useState<PlaybookLibraryType>('pitch');
  const [libraryTargetRoleId, setLibraryTargetRoleId] = useState<string>('');

  const contentRef = useRef<HTMLDivElement>(null);
  const lastAddedRef = useRef<HTMLDivElement>(null);

  const isEditMode = !!editingIndustry;

  // Initialize with general role
  const createGeneralRole = (): RoleData => ({
    id: 'general',
    displayName: 'General',
    pitchBullets: [''],
    advancedBullets: [''],
    questions: [{ text: '', tags: ['situation'] }],
    isExpanded: true
  });

  const createNewRole = (): RoleData => ({
    id: `role-${Date.now()}`,
    displayName: '',
    pitchBullets: [''],
    advancedBullets: [''],
    questions: [{ text: '', tags: ['situation'] }],
    isExpanded: true
  });

  // Convert existing industry data to modal format
  const loadIndustryData = (industry: SalesPlaybookIndustry) => {
    setIndustryDisplayName(industry.displayName);

    // Check if we have only a general role or multiple roles
    const hasOnlyGeneral = industry.roles.length === 1 && industry.roles[0].name === 'general';

    setUseMultipleRoles(!hasOnlyGeneral);

    if (hasOnlyGeneral) {
      // Load general role data
      const generalRole = industry.roles[0];
      setRoles([{
        id: 'general',
        displayName: 'General',
        pitchBullets: generalRole.pitch.length > 0
          ? generalRole.pitch.map(p => p.text)
          : [''],
        advancedBullets: generalRole.advanced?.length > 0
          ? generalRole.advanced.map(a => a.text)
          : [''],
        questions: generalRole.questions.length > 0
          ? generalRole.questions.map(q => ({ text: q.questionText, tags: q.tags }))
          : [{ text: '', tags: ['situation'] }],
        isExpanded: true
      }]);
    } else {
      // Load ALL roles (excluding general role which is auto-created)
      const rolesToLoad = industry.roles.filter(r => r.name !== 'general');

      if (rolesToLoad.length === 0) {
        setRoles([createNewRole()]);
      } else {
        setRoles(rolesToLoad.map(r => ({
          id: r._id,
          displayName: r.displayName,
          pitchBullets: r.pitch.length > 0 ? r.pitch.map(p => p.text) : [''],
          advancedBullets: r.advanced?.length > 0 ? r.advanced.map(a => a.text) : [''],
          questions: r.questions.length > 0
            ? r.questions.map(q => ({ text: q.questionText, tags: q.tags }))
            : [{ text: '', tags: ['situation'] }],
          isExpanded: true
        })));
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setSaving(false);

      if (editingIndustry) {
        loadIndustryData(editingIndustry);
      } else {
        // Reset form for new sales playbook
        setIndustryDisplayName('');
        setUseMultipleRoles(false);
        setRoles([createGeneralRole()]);
      }
    }
  }, [isOpen, editingIndustry]);

  // Scroll to bottom when adding new items
  const scrollToBottom = () => {
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }
    }, 100);
  };

  const handleRoleModeChange = (useMultiple: boolean) => {
    setUseMultipleRoles(useMultiple);
    if (useMultiple) {
      // Start with one empty role
      setRoles([createNewRole()]);
    } else {
      // Reset to general role
      setRoles([createGeneralRole()]);
    }
  };

  const handleAddRole = () => {
    setRoles([...roles, createNewRole()]);
    scrollToBottom();
  };

  const handleRemoveRole = (roleId: string) => {
    if (roles.length > 1) {
      setRoles(roles.filter(r => r.id !== roleId));
    }
  };

  const handleRoleNameChange = (roleId: string, displayName: string) => {
    setRoles(roles.map(r =>
      r.id === roleId ? { ...r, displayName } : r
    ));
  };

  const toggleRoleExpanded = (roleId: string) => {
    setRoles(roles.map(r =>
      r.id === roleId ? { ...r, isExpanded: !r.isExpanded } : r
    ));
  };

  // Pitch bullet handlers
  const handleAddPitchBullet = (roleId: string) => {
    setRoles(roles.map(r =>
      r.id === roleId ? { ...r, pitchBullets: [...r.pitchBullets, ''] } : r
    ));
    scrollToBottom();
  };

  const handleRemovePitchBullet = (roleId: string, index: number) => {
    setRoles(roles.map(r =>
      r.id === roleId && r.pitchBullets.length > 1
        ? { ...r, pitchBullets: r.pitchBullets.filter((_, i) => i !== index) }
        : r
    ));
  };

  const handlePitchBulletChange = (roleId: string, index: number, value: string) => {
    setRoles(roles.map(r => {
      if (r.id !== roleId) return r;
      const newBullets = [...r.pitchBullets];
      newBullets[index] = value;
      return { ...r, pitchBullets: newBullets };
    }));
  };

  // Advanced bullet handlers
  const handleAddAdvancedBullet = (roleId: string) => {
    setRoles(roles.map(r =>
      r.id === roleId ? { ...r, advancedBullets: [...r.advancedBullets, ''] } : r
    ));
    scrollToBottom();
  };

  const handleRemoveAdvancedBullet = (roleId: string, index: number) => {
    setRoles(roles.map(r =>
      r.id === roleId && r.advancedBullets.length > 1
        ? { ...r, advancedBullets: r.advancedBullets.filter((_, i) => i !== index) }
        : r
    ));
  };

  const handleAdvancedBulletChange = (roleId: string, index: number, value: string) => {
    setRoles(roles.map(r => {
      if (r.id !== roleId) return r;
      const newBullets = [...r.advancedBullets];
      newBullets[index] = value;
      return { ...r, advancedBullets: newBullets };
    }));
  };

  // Question handlers
  const handleAddQuestion = (roleId: string) => {
    setRoles(roles.map(r =>
      r.id === roleId
        ? { ...r, questions: [...r.questions, { text: '', tags: ['situation'] as SalesPlaybookTag[] }] }
        : r
    ));
    scrollToBottom();
  };

  const handleRemoveQuestion = (roleId: string, index: number) => {
    setRoles(roles.map(r =>
      r.id === roleId && r.questions.length > 1
        ? { ...r, questions: r.questions.filter((_, i) => i !== index) }
        : r
    ));
  };

  const handleQuestionTextChange = (roleId: string, index: number, value: string) => {
    setRoles(roles.map(r => {
      if (r.id !== roleId) return r;
      const newQuestions = [...r.questions];
      newQuestions[index] = { ...newQuestions[index], text: value };
      return { ...r, questions: newQuestions };
    }));
  };

  const handleQuestionTagToggle = (roleId: string, index: number, tag: SalesPlaybookTag) => {
    setRoles(roles.map(r => {
      if (r.id !== roleId) return r;
      const newQuestions = [...r.questions];
      const currentTags = newQuestions[index].tags;
      if (currentTags.includes(tag)) {
        if (currentTags.length > 1) {
          newQuestions[index] = { ...newQuestions[index], tags: currentTags.filter(t => t !== tag) };
        }
      } else {
        newQuestions[index] = { ...newQuestions[index], tags: [...currentTags, tag] };
      }
      return { ...r, questions: newQuestions };
    }));
  };

  const openLibraryPanel = (roleId: string, type: PlaybookLibraryType) => {
    setLibraryTargetRoleId(roleId);
    setLibraryPanelType(type);
    setLibraryPanelOpen(true);
  };

  const handleLibrarySelect = (texts: string[]) => {
    if (!libraryTargetRoleId || texts.length === 0) return;

    setRoles(roles.map(r => {
      if (r.id !== libraryTargetRoleId) return r;

      if (libraryPanelType === 'pitch') {
        const existing = r.pitchBullets.filter(b => b.trim());
        return { ...r, pitchBullets: [...existing, ...texts] };
      }
      if (libraryPanelType === 'advanced') {
        const existing = r.advancedBullets.filter(b => b.trim());
        return { ...r, advancedBullets: [...existing, ...texts] };
      }
      if (libraryPanelType === 'question') {
        const existing = r.questions.filter(q => q.text.trim());
        const newQuestions = texts.map(text => ({
          text,
          tags: ['situation'] as SalesPlaybookTag[]
        }));
        return { ...r, questions: [...existing, ...newQuestions] };
      }
      return r;
    }));
  };

  const handlePitchDragEnd = (roleId: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRoles(prev => prev.map(r => {
      if (r.id !== roleId) return r;
      return { ...r, pitchBullets: arrayMove(r.pitchBullets, active.id as number, over.id as number) };
    }));
  };

  const handleAdvancedDragEnd = (roleId: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRoles(prev => prev.map(r => {
      if (r.id !== roleId) return r;
      return { ...r, advancedBullets: arrayMove(r.advancedBullets, active.id as number, over.id as number) };
    }));
  };

  const handleQuestionDragEnd = (roleId: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRoles(prev => prev.map(r => {
      if (r.id !== roleId) return r;
      return { ...r, questions: arrayMove(r.questions, active.id as number, over.id as number) };
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!industryDisplayName.trim()) {
      setError('Industry name is required');
      return;
    }

    // Validate roles - keep ALL roles, only filter out those without a name in multiple roles mode
    const validRoles = roles.map(role => ({
      // Include original ID for roles loaded from existing industry (not for newly created roles)
      originalId: role.id !== 'general' && !role.id.startsWith('role-') ? role.id : undefined,
      displayName: useMultipleRoles ? role.displayName.trim() : 'General',
      pitchBullets: role.pitchBullets.filter(b => b.trim()),
      advancedBullets: role.advancedBullets.filter(b => b.trim()),
      questions: role.questions.filter(q => q.text.trim())
    })).filter(role => {
      // For multiple roles mode, require a role name (but allow empty content)
      if (useMultipleRoles && !role.displayName) return false;
      // Keep all roles with a name, even if content is empty
      return true;
    });

    if (validRoles.length === 0) {
      setError('Please add at least one role');
      return;
    }

    if (useMultipleRoles) {
      const emptyRoleNames = roles.filter(r => !r.displayName.trim());
      if (emptyRoleNames.length > 0) {
        setError('Please provide a name for all roles');
        return;
      }
    }

    setSaving(true);
    try {
      await onSave({
        industryDisplayName: industryDisplayName.trim(),
        roles: validRoles
      });
    } catch (err: any) {
      setError(err.message || 'Failed to save sales playbook');
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const renderRoleContent = (role: RoleData) => (
    <div className="space-y-4 mt-3">
      {/* Intro Pitch */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-600">Intro Pitch</label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => openLibraryPanel(role.id, 'pitch')}
              className="text-purple-600 hover:text-purple-700 text-xs flex items-center gap-1"
            >
              <LibraryIcon style={{ fontSize: '0.875rem' }} />
              From Library
            </button>
            <button
              type="button"
              onClick={() => handleAddPitchBullet(role.id)}
              className="text-blue-600 hover:text-blue-700 text-xs flex items-center gap-1"
            >
              <PlusIcon style={{ fontSize: '0.875rem' }} />
              Add Intro Pitch
            </button>
          </div>
        </div>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={(event) => handlePitchDragEnd(role.id, event)}
        >
          <SortableContext
            items={role.pitchBullets.map((_, i) => i)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {role.pitchBullets.map((bullet, index) => (
                <SortableBulletItem
                  key={index}
                  id={index}
                  value={bullet}
                  placeholder={`Pitch point ${index + 1}...`}
                  canRemove={role.pitchBullets.length > 1}
                  onChange={(value) => handlePitchBulletChange(role.id, index, value)}
                  onRemove={() => handleRemovePitchBullet(role.id, index)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Qualifying Questions */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-600">Qualifying Questions</label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => openLibraryPanel(role.id, 'question')}
              className="text-purple-600 hover:text-purple-700 text-xs flex items-center gap-1"
            >
              <LibraryIcon style={{ fontSize: '0.875rem' }} />
              From Library
            </button>
            <button
              type="button"
              onClick={() => handleAddQuestion(role.id)}
              className="text-blue-600 hover:text-blue-700 text-xs flex items-center gap-1"
            >
              <PlusIcon style={{ fontSize: '0.875rem' }} />
              Add Question
            </button>
          </div>
        </div>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={(event) => handleQuestionDragEnd(role.id, event)}
        >
          <SortableContext
            items={role.questions.map((_, i) => i)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {role.questions.map((question, index) => (
                <SortableQuestionItem
                  key={index}
                  id={index}
                  question={question}
                  index={index}
                  canRemove={role.questions.length > 1}
                  onTextChange={(value) => handleQuestionTextChange(role.id, index, value)}
                  onRemove={() => handleRemoveQuestion(role.id, index)}
                  onTagToggle={(tag) => handleQuestionTagToggle(role.id, index, tag)}
                  tagLabels={TAG_LABELS}
                  allTags={SALES_PLAYBOOK_TAGS}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Advanced */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-600">Deal Progression</label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => openLibraryPanel(role.id, 'advanced')}
              className="text-purple-600 hover:text-purple-700 text-xs flex items-center gap-1"
            >
              <LibraryIcon style={{ fontSize: '0.875rem' }} />
              From Library
            </button>
            <button
              type="button"
              onClick={() => handleAddAdvancedBullet(role.id)}
              className="text-blue-600 hover:text-blue-700 text-xs flex items-center gap-1"
            >
              <PlusIcon style={{ fontSize: '0.875rem' }} />
              Add Deal Progression
            </button>
          </div>
        </div>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={(event) => handleAdvancedDragEnd(role.id, event)}
        >
          <SortableContext
            items={role.advancedBullets.map((_, i) => i)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {role.advancedBullets.map((bullet, index) => (
                <SortableBulletItem
                  key={index}
                  id={index}
                  value={bullet}
                  placeholder={`Deal progression point ${index + 1}...`}
                  canRemove={role.advancedBullets.length > 1}
                  onChange={(value) => handleAdvancedBulletChange(role.id, index, value)}
                  onRemove={() => handleRemoveAdvancedBullet(role.id, index)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <form onSubmit={handleSubmit}>
            {/* Header - Fixed */}
            <div className="bg-white px-4 pt-4 pb-3 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  {isEditMode ? 'Edit Sales Playbook' : 'Add Sales Playbook'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <CloseIcon />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div
              ref={contentRef}
              className="px-4 py-4 max-h-[65vh] overflow-y-auto"
            >
              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-5">
                {/* Industry */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Industry *
                  </label>
                  <input
                    type="text"
                    value={industryDisplayName}
                    onChange={(e) => setIndustryDisplayName(e.target.value)}
                    placeholder="e.g., Airport"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    list="existing-industries"
                    autoFocus
                    disabled={isEditMode}
                  />
                  {!isEditMode && (
                    <>
                      <datalist id="existing-industries">
                        {existingIndustries.map(industry => (
                          <option key={industry} value={industry} />
                        ))}
                      </datalist>
                      <p className="mt-1 text-xs text-gray-500">
                        Type a new industry name
                      </p>
                    </>
                  )}
                </div>

                {/* Role Mode Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role Structure
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="roleMode"
                        checked={!useMultipleRoles}
                        onChange={() => handleRoleModeChange(false)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm text-gray-700">General role only</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="roleMode"
                        checked={useMultipleRoles}
                        onChange={() => handleRoleModeChange(true)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm text-gray-700">Multiple roles</span>
                    </label>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {useMultipleRoles
                      ? 'Add specific roles (e.g., VP of Operations, Facilities Manager) with their own pitch and questions'
                      : 'Use a single "General" role for all pitch and questions'
                    }
                  </p>
                </div>

                {/* Roles Section */}
                {useMultipleRoles ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700">Roles</label>
                      <button
                        type="button"
                        onClick={handleAddRole}
                        className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center gap-1"
                      >
                        <PlusIcon style={{ fontSize: '1rem' }} />
                        Add Role
                      </button>
                    </div>

                    {roles.map((role, roleIndex) => (
                      <div
                        key={role.id}
                        className="border border-gray-300 rounded-lg overflow-hidden"
                        ref={roleIndex === roles.length - 1 ? lastAddedRef : null}
                      >
                        {/* Role Header */}
                        <div
                          className="bg-gray-100 px-3 py-2 flex items-center justify-between cursor-pointer"
                          onClick={() => toggleRoleExpanded(role.id)}
                        >
                          <div className="flex items-center gap-2 flex-1">
                            {role.isExpanded ? (
                              <ExpandLessIcon className="text-gray-500" style={{ fontSize: '1.25rem' }} />
                            ) : (
                              <ExpandMoreIcon className="text-gray-500" style={{ fontSize: '1.25rem' }} />
                            )}
                            <input
                              type="text"
                              value={role.displayName}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleRoleNameChange(role.id, e.target.value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Role name (e.g., VP of Operations)"
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          {roles.length > 1 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveRole(role.id);
                              }}
                              className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            >
                              <TrashIcon style={{ fontSize: '1.25rem' }} />
                            </button>
                          )}
                        </div>

                        {/* Role Content */}
                        {role.isExpanded && (
                          <div className="px-3 pb-3 bg-white">
                            {renderRoleContent(role)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  /* General Role Content */
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">General Role Content</h4>
                    {roles[0] && renderRoleContent(roles[0])}
                  </div>
                )}
              </div>

              {/* Spacer for scroll */}
              <div ref={lastAddedRef} />
            </div>

            {/* Footer - Fixed */}
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse gap-2 border-t">
              <button
                type="submit"
                disabled={saving}
                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:w-auto sm:text-sm ${
                  saving
                    ? 'bg-blue-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }`}
              >
                {saving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Add Sales Playbook')}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Library Selection Panel */}
      <LibrarySelectPanel
        type={libraryPanelType}
        isOpen={libraryPanelOpen}
        onClose={() => setLibraryPanelOpen(false)}
        onSelect={handleLibrarySelect}
      />
    </div>
  );
};

export default AddSalesPlaybookModal;
